import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setIsOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
    }`}>
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <span className="font-heading text-2xl font-bold text-foreground-900 tracking-tight">
              Kakobuy<span className="text-primary-500">Finds</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">
              {t('nav.home')}
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 text-foreground-700 hover:text-foreground-900 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                <span>{t('nav.categories')}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 z-50 max-h-[480px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-50 overflow-hidden shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                      </span>
                      <span>{cat.name}</span>
                      <i className="ri-external-link-line text-xs ml-auto text-foreground-400"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/artykul/kakobuy-spreadsheet-poradnik/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">
              {t('nav.shopping_guide')}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-foreground-900 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.spreadsheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.buy_now')}
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-72px)] overflow-y-auto overscroll-contain">
          <div className="px-4 py-3 space-y-1.5">
            <Link
              to="/"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-secondary-50 rounded-xl transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                <i className="ri-home-4-line text-base"></i>
              </span>
              <span>{t('nav.home')}</span>
            </Link>
            <Link
              to="/artykul/kakobuy-spreadsheet-poradnik/"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-secondary-50 rounded-xl transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500">
                <i className="ri-book-open-line text-base"></i>
              </span>
              <span>{t('nav.shopping_guide')}</span>
            </Link>
          </div>

          <div className="px-4 py-2">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 py-2">{t('nav.categories')}</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-2.5 py-2 text-sm text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-50 overflow-hidden shrink-0">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                  </span>
                  <span className="truncate text-xs">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="px-4 py-3 border-t border-background-200/70 flex flex-col gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-3 bg-foreground-900 text-background-50 text-sm font-medium rounded-full text-center cursor-pointer"
            >
              {t('nav.spreadsheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full text-center cursor-pointer"
            >
              {t('nav.buy_now')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}